import { supabase } from '../lib/supabase';
import { cartService } from './cartService';

export const bundleService = {
  // Fetch all active bundles (Public)
  async getActiveBundles() {
    try {
      // 1. Fetch bundles without ambiguous join
      const { data: bundles, error } = await supabase
        .from('bundles')
        .select(`
          *,
          items:bundle_items (
            id,
            product_id,
            component_id,
            quantity,
            product:products (id, name, thumbnail_path, price, category_id),
            component:components (
              id, name, custom_name, images_url, price,
              product_links:product_components (product:products (id, name, thumbnail_path))
            )
          )
        `)
        .eq('active', true);

      if (error) throw error;

      // 2. Manual Join for Seller Info and Prices
      const sellerIds = [...new Set(bundles.map(b => b.seller_id).filter(Boolean))];
      let sellerMap = {};
      let sellerProducts = [];
      let sellerComps = [];
      
      if (sellerIds.length > 0) {
          const { data: sellers } = await supabase
            .from('seller_accounts')
            .select('id, business_name, business_slug')
            .in('id', sellerIds);
          
          if (sellers) {
              sellerMap = sellers.reduce((acc, s) => {
                  acc[s.id] = s;
                  return acc;
              }, {});
          }

          // Fetch all product items across these sellers
          const productIds = bundles.flatMap(b => b.items.map(i => i.product?.id).filter(Boolean));
          if (productIds.length > 0) {
              const { data: sp } = await supabase.from('seller_products').select('product_id, price, seller_image_url, seller_id').in('product_id', productIds).in('seller_id', sellerIds);
              if (sp) sellerProducts = sp;
          }

          const componentIds = bundles.flatMap(b => b.items.map(i => i.component?.id).filter(Boolean));
          if (componentIds.length > 0) {
              const { data: sc } = await supabase.from('seller_components').select('component_id, price, custom_name, images_url, seller_id').in('component_id', componentIds).in('seller_id', sellerIds);
              if (sc) sellerComps = sc;
          }
      }

      // 3. Merge and Process
      const mergedBundles = bundles.map(b => ({
          ...b,
          seller: sellerMap[b.seller_id] || null,
          sellerProducts: sellerProducts.filter(sp => sp.seller_id === b.seller_id),
          sellerComps: sellerComps.filter(sc => sc.seller_id === b.seller_id)
      }));

      return this.processBundles(mergedBundles);
    } catch (err) {
      console.error('Error fetching bundles:', err);
      return [];
    }
  },

  // Fetch a single bundle with items
  async getBundle(id) {
    const { data, error } = await supabase
      .from('bundles')
      .select('*, items:bundle_items(*, product:products(id, name, price, thumbnail_path), component:components(id, name, custom_name, price, images_url, product_links:product_components(product:products(id, name, thumbnail_path))))')
      .eq('id', id)
      .single();

    if (error) throw error;
    
    if (data && data.seller_id && data.items) {
      const productIds = data.items.map(i => i.product_id).filter(Boolean);
      const componentIds = data.items.map(i => i.component_id).filter(Boolean);

      let sellerProducts = [];
      let sellerComps = [];

      if (productIds.length > 0) {
        const { data: sp } = await supabase.from('seller_products').select('product_id, price, seller_image_url').in('product_id', productIds).eq('seller_id', data.seller_id);
        if (sp) sellerProducts = sp;
      }
      
      if (componentIds.length > 0) {
        const { data: sc } = await supabase.from('seller_components').select('component_id, price, custom_name, images_url').in('component_id', componentIds).eq('seller_id', data.seller_id);
        if (sc) sellerComps = sc;
      }

      data.items = data.items.map(item => {
        let price = 0;
        let thumbnail = null;
        let name = 'Unknown Item';
        let originalName = null;

        if (item.component_id) {
            const sc = sellerComps.find(c => c.component_id === item.component_id);
            price = sc?.price ?? 0;
            thumbnail = (sc?.images_url && sc.images_url[0]) || (item.component?.images_url && item.component?.images_url[0]) || null;
            name = sc?.custom_name || item.component?.custom_name || item.component?.name || 'Unknown Component';
            let parentProductName = item.component?.product_links?.[0]?.product?.name;
            if (parentProductName && name) {
                 name = `${name} (for ${parentProductName})`;
            }
            originalName = item.component?.name;
            
            if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/assets') && !thumbnail.startsWith('/api')) {
                 const { data: urlData } = supabase.storage.from('component-images').getPublicUrl(thumbnail);
                 thumbnail = urlData.publicUrl;
            }
        } else if (item.product_id) {
            const sp = sellerProducts.find(p => p.product_id === item.product_id);
            price = sp?.price ?? 0;
            thumbnail = (sp?.seller_image_url && sp.seller_image_url[0]) || item.product?.thumbnail_path || null;
            name = item.product?.name || 'Unknown Product';
            originalName = item.product?.name;
            
            if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/assets') && !thumbnail.startsWith('/api')) {
                 const bucket = (sp?.seller_image_url && sp.seller_image_url[0]) ? 'seller-product-images' : 'product-thumbnails';
                 const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(thumbnail);
                 thumbnail = urlData.publicUrl;
            }
        }

        return {
          tempId: item.id,
          id: item.id,
          product_id: item.product_id,
          productId: item.product_id,
          component_id: item.component_id,
          componentId: item.component_id,
          quantity: item.quantity,
          name: name,
          originalName: originalName,
          price: price,
          thumbnail: thumbnail
        };
      });
      
      // Calculate totals for camelCase properties
      const totalOriginalPrice = data.items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
      const bundlePrice = data.bundle_price || (totalOriginalPrice * (1 - (data.discount_percentage || 0) / 100));
      
      // Resolve bundle image if it's just a file path
      let resolvedImageUrl = data.image_url;
      if (resolvedImageUrl && !resolvedImageUrl.startsWith('http') && !resolvedImageUrl.startsWith('/assets') && !resolvedImageUrl.startsWith('/api')) {
           const { data: urlData } = supabase.storage.from('product-thumbnails').getPublicUrl(resolvedImageUrl);
           resolvedImageUrl = urlData.publicUrl;
      }
      
      return {
          ...data,
          bundlePrice,
          originalPrice: totalOriginalPrice,
          savings: totalOriginalPrice - bundlePrice,
          itemCount: data.items.reduce((acc, i) => acc + i.quantity, 0),
          imageUrl: resolvedImageUrl,
          image: resolvedImageUrl, // Provide both for compatibility
          items: data.items
      };
    }
    
    return data;
  },

  async createBundle(bundleData) {
      // 1. Insert Bundle
      const { data: bundle, error } = await supabase
          .from('bundles')
          .insert({
              name: bundleData.name,
              description: bundleData.description,
              category: bundleData.category,
              bundle_price: bundleData.bundle_price,
              discount_percentage: bundleData.discount_percentage,
              image_url: bundleData.image_url,
              active: bundleData.active,
              created_by: bundleData.created_by, // user_id
              seller_id: bundleData.seller_id // seller_accounts id
          })
          .select()
          .single();
      
      if (error) throw error;

      // 2. Insert Items
      if (bundleData.items && bundleData.items.length > 0) {
          const items = bundleData.items.map(item => ({
              bundle_id: bundle.id,
              product_id: item.product_id,
              component_id: item.component_id,
              quantity: item.quantity
          }));
          
          const { error: itemsError } = await supabase.from('bundle_items').insert(items);
          if (itemsError) throw itemsError;
      }
      return bundle;
  },

  async updateBundle(id, bundleData) {
      const { items, ...bundleFields } = bundleData;

      // 1. Update bundle details
      const { error: bundleError } = await supabase
          .from('bundles')
          .update({
              name: bundleFields.name,
              description: bundleFields.description,
              category: bundleFields.category,
              bundle_price: bundleFields.bundle_price,
              discount_percentage: bundleFields.discount_percentage,
              image_url: bundleFields.image_url,
              active: bundleFields.active
          })
          .eq('id', id);

      if (bundleError) throw bundleError;

      // 2. Delete existing items
      const { error: deleteError } = await supabase
          .from('bundle_items')
          .delete()
          .eq('bundle_id', id);

      if (deleteError) throw deleteError;

      // 3. Insert new items
      if (items && items.length > 0) {
          const bundleItems = items.map(item => ({
              bundle_id: id,
              product_id: item.product_id,
              component_id: item.component_id,
              quantity: item.quantity || 1
          }));

          const { error: itemsError } = await supabase
              .from('bundle_items')
              .insert(bundleItems);

          if (itemsError) throw itemsError;
      }

      return true;
  },

  async deleteBundle(id) {
      // Delete child bundle items first to prevent PostgreSQL Foreign Key Constraint errors
      const { error: itemsError } = await supabase
          .from('bundle_items')
          .delete()
          .eq('bundle_id', id);
          
      if (itemsError) throw itemsError;

      const { error } = await supabase
          .from('bundles')
          .delete()
          .eq('id', id);

      if (error) throw error;
      return true;
  },

  // Helper to process bundles for display (calculates totals, images, etc.)
  processBundles(bundles) {
    return bundles.map(bundle => {
        let calculatedOriginalPrice = 0;
        let itemCount = 0;
        
        const sellerProducts = bundle.sellerProducts || [];
        const sellerComps = bundle.sellerComps || [];

        const processedItems = bundle.items.map(item => {
          const detail = item.product || item.component;
          let price = 0;
          let thumbnail = null;
          let name = 'Unknown Item';

          if (item.component) {
              const sc = sellerComps.find(c => c.component_id === item.component.id);
              price = sc?.price || item.component?.price || 0;
              thumbnail = (sc?.images_url && sc.images_url[0]) || (item.component?.images_url && item.component?.images_url[0]) || null;
              name = sc?.custom_name || item.component?.custom_name || item.component?.name || 'Unknown Component';
              let parentProductName = item.component?.product_links?.[0]?.product?.name;
              if (parentProductName && name) {
                   name = `${name} (for ${parentProductName})`;
              }
              
              if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/assets') && !thumbnail.startsWith('/api')) {
                   const { data: urlData } = supabase.storage.from('component-images').getPublicUrl(thumbnail);
                   thumbnail = urlData.publicUrl;
              }
          } else if (item.product) {
              const sp = sellerProducts.find(p => p.product_id === item.product.id);
              price = sp?.price ?? item.product?.price ?? 0;
              thumbnail = (sp?.seller_image_url && sp.seller_image_url[0]) || item.product?.thumbnail_path || null;
              name = item.product?.name || 'Unknown Product';

              if (thumbnail && !thumbnail.startsWith('http') && !thumbnail.startsWith('/assets') && !thumbnail.startsWith('/api')) {
                   const bucket = (sp?.seller_image_url && sp.seller_image_url[0]) ? 'seller-product-images' : 'product-thumbnails';
                   const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(thumbnail);
                   thumbnail = urlData.publicUrl;
              }
          }
          
          calculatedOriginalPrice += price * item.quantity;
          itemCount += item.quantity;
          
          return {
            ...item,
            thumbnail,
            thumbnailAlt: detail?.name,
            name,
            price,
            productId: item.product?.id || item.component?.product?.id,
            componentId: item.component?.id
          };
        });

        const originalPrice = bundle.original_price || calculatedOriginalPrice;
        const bundlePrice = bundle.bundle_price || (originalPrice * (1 - (bundle.discount_percentage || 0) / 100));
        const savings = originalPrice - bundlePrice;

        let bundleImage = bundle.image_url || (processedItems.length > 0 ? processedItems[0].thumbnail : null);
        if (bundleImage && !bundleImage.startsWith('http') && !bundleImage.startsWith('/assets') && !bundleImage.startsWith('/api')) {
             const { data: urlData } = supabase.storage.from('product-thumbnails').getPublicUrl(bundleImage);
             bundleImage = urlData.publicUrl;
        }

        return {
          id: bundle.id,
          name: bundle.name,
          category: bundle.category,
          description: bundle.description,
          image: bundleImage,
          imageAlt: bundle.image_alt || bundle.name,
          itemCount,
          bundlePrice,
          originalPrice,
          savings: savings > 0 ? savings : 0,
          items: processedItems,
          type: 'bundle',
          seller_id: bundle.seller_id,
          sellerBusinessName: bundle.seller?.business_name || 'OpenGadget',
          sellerBusinessSlug: bundle.seller?.business_slug
        };
      });
  },

  // Add bundle to cart
  async addBundleToCart(userId, bundleId) {
    try {
      // 0. Fetch Bundle Details for Discount Calculation
      const { data: bundle, error: bundleError } = await supabase
        .from('bundles')
        .select('bundle_price, discount_percentage, seller_id')
        .eq('id', bundleId)
        .single();
      
      if (bundleError || !bundle) throw new Error('Bundle not found');

      // 1. Fetch bundle items
      const { data: bundleItems, error } = await supabase
        .from('bundle_items')
        .select(`
          product_id, component_id, quantity,
          product:products (id, created_by, price),
          component:components (id, created_by, price)
        `)
        .eq('bundle_id', bundleId);
        
      if (error) throw error;
      if (!bundleItems?.length) throw new Error('Bundle has no items');

      // Fetch accurate prices for these items from seller tables
      const productIds = bundleItems.map(i => i.product_id).filter(Boolean);
      const componentIds = bundleItems.map(i => i.component_id).filter(Boolean);

      let sellerProducts = [];
      let sellerComps = [];

      if (productIds.length > 0) {
        const { data: sp } = await supabase.from('seller_products').select('product_id, price').in('product_id', productIds).eq('seller_id', bundle.seller_id);
        if (sp) sellerProducts = sp;
      }
      
      if (componentIds.length > 0) {
        const { data: sc } = await supabase.from('seller_components').select('component_id, price').in('component_id', componentIds).eq('seller_id', bundle.seller_id);
        if (sc) sellerComps = sc;
      }

      // Calculate discount factor based on current item prices
      let totalOriginalPrice = 0;
      bundleItems.forEach(item => {
          let price = 0;

          if (item.component_id) {
            const sc = sellerComps.find(c => c.component_id === item.component_id);
            if (!sc) throw new Error(`Bundle item (Component) is not currently offered by this seller.`);
            price = sc.price;
          } else if (item.product_id) {
            const sp = sellerProducts.find(p => p.product_id === item.product_id);
            if (!sp) throw new Error(`Bundle item (Product) is not currently offered by this seller.`);
            price = sp.price;
          }
          totalOriginalPrice += Number(price) * Number(item.quantity || 1);
      });

      let discountFactor = 1;
      // If bundle_price is set, distribute it proportionally
      if (bundle.bundle_price != null) {
          if (totalOriginalPrice > 0) {
              discountFactor = Number(bundle.bundle_price) / totalOriginalPrice;
          }
      } 
      // Else if discount_percentage is set
      else if (bundle.discount_percentage) {
          discountFactor = 1 - (Number(bundle.discount_percentage) / 100);
      }

      // 2. Add items to cart...
      await Promise.all(bundleItems.map(async (item) => {
        let price = 0;

        if (item.component_id) {
          const sc = sellerComps.find(c => c.component_id === item.component_id);
          if (!sc) throw new Error(`Bundle item (Component) is not currently offered by this seller.`);
          price = sc.price;
        } else if (item.product_id) {
          const sp = sellerProducts.find(p => p.product_id === item.product_id);
          if (!sp) throw new Error(`Bundle item (Product) is not currently offered by this seller.`);
          price = sp.price;
        }

        // Calculate discounted price for this item
        const originalItemPrice = Number(price);
        const discountedItemPrice = originalItemPrice * discountFactor;

        return cartService.addToCart({
          userId: userId,
          productId: item.product_id ? String(item.product_id) : null,
          componentId: item.component_id ? String(item.component_id) : null,
          quantity: item.quantity,
          sellerId: bundle.seller_id,
          bundleId: bundleId, // Track bundle association
          price: discountedItemPrice // Override price
        });
      }));
      
      return { success: true };
    } catch (err) {
      console.error('Error adding bundle to cart:', err);
      throw err;
    }
  }
};
